import { useQuery } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { timeKeepingApi } from '../../../../apis';
import { MONTH_YEAR_FORMAT } from '../../../../constants/common';
import { TIME_KEEPING_HISTORY_STATISTIC } from '../../../../constants/QueryKey';
import { ADMIN_ROUTE_NAME } from '../../../../constants/RouteName';
import type { RootState } from '../../../../store';
import { formatMinutesToHoursAndMinutes } from '../../../../utils';
import './style.scss';

interface SearchFilterEmployeeHistory {
  month: string;
  isUsersScreen?: boolean;
}

interface Props {
  initialFilter: SearchFilterEmployeeHistory;
}

const AttendanceOption: React.FC<Props> = ({ initialFilter }) => {
  const intl = useIntl();
  const { isUsersScreen } = initialFilter;
  const { employeeId } = useParams<{ employeeId: string }>();
  const location = useLocation();
  const userId = useSelector((state: RootState) => state.auth.authUser?.id);
  const isManagerRoute = location.pathname.includes(ADMIN_ROUTE_NAME.TIMEKEEPING_CONFIRMATION);
  const id = isManagerRoute || isUsersScreen ? userId : employeeId;

  const { data: historyStatisticsData } = useQuery({
    queryKey: [TIME_KEEPING_HISTORY_STATISTIC, initialFilter],
    queryFn: () =>
      timeKeepingApi.timekeepingControllerGetEmployeeTimekeepingHistoryStatistic(
        id as string,
        dayjs(initialFilter.month).format(MONTH_YEAR_FORMAT),
      ),
    enabled: !!id && !!initialFilter.month,
  });

  const data = historyStatisticsData?.data?.data
    ? [
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.numberOfWorkDays' }),
          value: historyStatisticsData?.data?.data?.numberOfWorkDays || 0,
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.numberOfUsersWorkDaysOnDayOff' }),
          value: historyStatisticsData?.data?.data?.numberOfUsersWorkDaysOnDayOff || 0,
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.amountOfOvertimeRegulatedDaysOff' }),
          value:
            formatMinutesToHoursAndMinutes(historyStatisticsData?.data?.data?.amountOfOvertimeRegulatedDaysOff) ||
            '00:00',
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.numberOfUsersWorkDays' }),
          value: historyStatisticsData?.data?.data?.numberOfUsersWorkDays || 0,
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.numberOfUsersCompensatoryLeaveDays' }),
          value: historyStatisticsData?.data?.data?.numberOfUsersCompensatoryLeaveDays || 0,
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.amountOfOvertimeInDayLegalDaysOff' }),
          value:
            formatMinutesToHoursAndMinutes(historyStatisticsData?.data?.data?.amountOfOvertimeInDayLegalDaysOff) ||
            '00:00',
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.numberOfUsersRegularWorkDays' }),
          value: historyStatisticsData?.data?.data?.numberOfUsersRegularWorkDays || 0,
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.numberOfUsersAnnualLeaveDays' }),
          value: historyStatisticsData?.data?.data?.numberOfUsersAnnualLeaveDays || 0,
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.amountOfOvertimeInNightLegalDaysOff' }),
          value:
            formatMinutesToHoursAndMinutes(historyStatisticsData?.data?.data?.amountOfOvertimeInNightLegalDaysOff) ||
            '00:00',
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.numberOfUsersAbsenceDays' }),
          value: historyStatisticsData?.data?.data?.numberOfUsersAbsenceDays || 0,
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.amountOfOvertimeInDay' }),
          value: formatMinutesToHoursAndMinutes(historyStatisticsData?.data?.data?.amountOfOvertimeInDay) || '00:00',
        },
        {
          label: intl.formatMessage({
            id: 'attendance.attendance.option.label.amountOfOvertimeLegalDaysOffAbove60Hours',
          }),
          value:
            formatMinutesToHoursAndMinutes(
              historyStatisticsData?.data?.data?.amountOfOvertimeLegalDaysOffAbove60Hours,
            ) || '00:00',
        },
        {
          label: intl.formatMessage({
            id: 'attendance.attendance.option.label.numberOfUsersGoingLateAndLeaveSoonDays',
          }),
          value: historyStatisticsData?.data?.data?.numberOfUsersGoingLateAndLeaveSoonDays || 0,
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.amountOfOvertimeInNight' }),
          value: formatMinutesToHoursAndMinutes(historyStatisticsData?.data?.data?.amountOfOvertimeInNight) || '00:00',
        },
        {
          label: intl.formatMessage({ id: 'attendance.attendance.option.label.numberOfLeaveDayRemaining' }),
          value: historyStatisticsData?.data?.data?.numberOfLeaveDayRemaining || 0,
        },
      ]
    : [];

  const columns = data.reduce((acc: { label: string; value: string | number }[][], _, index) => {
    if (index % 3 === 0) {
      acc.push(data.slice(index, index + 3));
    }
    return acc;
  }, []);

  return (
    <Row gutter={[16, 16]} className="attendance-layout" wrap={false}>
      {columns.map((columnData, columnIndex) => (
        <Col key={columnIndex} className="attendance-layout_column">
          <Row className="attendance-layout_column-item">
            <Col span={18} className="attendance-layout_column-left">
              {columnData.map((item, index) => (
                <Row key={index} className="attendance-layout_row-left">
                  {item.label}
                </Row>
              ))}
            </Col>
            <Col span={6} className="attendance-layout_column-right">
              {columnData.map((item, index) => (
                <Row key={index} className="attendance-layout_row-right">
                  {item.value}
                </Row>
              ))}
            </Col>
          </Row>
        </Col>
      ))}
    </Row>
  );
};

export default AttendanceOption;
